import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Button,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  TableSortLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  useTSQUOMTypeList,
  useTSMDeactivateUOMType,
  useTSMReactivateUOMType,
} from "../../hooks/useTSUnits";
import { UOMTypeFull } from "../../types/uomTypes";
import { paginationDefaults } from "../../config/settingsDefaults";

export const PageUOMTypeList: React.FC = () => {
  const navigate = useNavigate();

  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState<
    "active" | "inactive" | "all"
  >("active");

  // Sorting
  const [orderBy, setOrderBy] = useState<string>("");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  // UOM Query Hook
  const {
    data: unitTypeResponse,
    isLoading,
    error: queryError,
    isPlaceholderData,
  } = useTSQUOMTypeList({
    skip: -1,
    limit: -1,
    active_status: activeFilter,
    filters: [[]],
  });

  const deactivateUOMTypeMutation = useTSMDeactivateUOMType();
  const restoreUOMTypeMutation = useTSMReactivateUOMType();

  const uomTypes = unitTypeResponse?.items || [];
  const totalCount = unitTypeResponse?.total || 0;

  // Handle Mutations
  const handleDelete = async (id: string) => {
    if (
      !window.confirm(
        "Are you sure you want to de-activate this UOM Type? This will also de-activate all associated UOM Units."
      )
    ) {
      return;
    }

    try {
      await deactivateUOMTypeMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to de-activate UOM Type:", err);
      setError(err.response?.data?.detail || "Failed to de-activate UOM Type.");
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await restoreUOMTypeMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to restore UOM Type:", err);
      setError(err.response?.data?.detail || "Failed to restore UOM Type");
    }
  };

  // Handle pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Handle sorting
  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Get sortable value from UOM type
  const getSortValue = (uomType: UOMTypeFull, property: string): any => {
    switch (property) {
      case "type_name":
        return uomType.type_name;
      case "base_unit_name":
        return uomType.base_unit?.unit_name;
      case "unit_count":
        return uomType.all_units?.length || 0;
      case "item_count":
        return uomType.item_count;
      case "created_at":
        return new Date(uomType.created_at);
      case "status":
        if (!uomType.is_record_active) return "inactive";
        return "active";
      default:
        return "";
    }
  };

  // Filter and sort UOM types
  const filteredAndSortedUomTypes = useMemo(() => {
    // First filter
    let filtered = uomTypes.filter((uomType) => {
      const matchesSearch =
        !searchTerm ||
        uomType.type_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        uomType.base_unit?.unit_name ||
        "".toLowerCase().includes(searchTerm.toLowerCase()) ||
        (uomType.base_unit?.unit_symbol || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      return matchesSearch;
    });

    // Then sort
    if (orderBy) {
      const sorted = [...filtered].sort((a, b) => {
        const aValue = getSortValue(a, orderBy);
        const bValue = getSortValue(b, orderBy);

        if (aValue === bValue) return 0;

        if (order === "asc") {
          return aValue < bValue ? -1 : 1;
        } else {
          return aValue > bValue ? -1 : 1;
        }
      });
      const start = pagination.size * pagination.page;
      const end = start + pagination.size;
      return sorted.slice(start, end);
    } else {
      const start = pagination.size * pagination.page;
      const end = start + pagination.size;
      return filtered.slice(start, end);
    }
  }, [uomTypes, searchTerm, orderBy, order, pagination]);

  // Get status chip
  const getStatusChip = (uomType: UOMTypeFull) => {
    if (!uomType.is_record_active) {
      return <Chip label="Inactive" color="warning" size="small" />;
    }
    return <Chip label="Active" color="success" size="small" />;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          UOM Type Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/inventory/uoms/form?mode=create")}
        >
          Add UOM Type
        </Button>
      </Box>

      {/* Error Alert */}
      {(error || queryError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || queryError?.message || "An error occurred"}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <TextField
            label="Search UOM types..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 250 }}
            placeholder="Search by type name or base unit"
          />

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value as any)}
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Data Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sortDirection={orderBy === "type_name" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "type_name"}
                    direction={orderBy === "type_name" ? order : "asc"}
                    onClick={() => handleSort("type_name")}
                  >
                    Type Name
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === "base_unit" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "base_unit"}
                    direction={orderBy === "base_unit" ? order : "asc"}
                    onClick={() => handleSort("base_unit")}
                  >
                    Base Unit
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === "unit_count" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "unit_count"}
                    direction={orderBy === "unit_count" ? order : "asc"}
                    onClick={() => handleSort("unit_count")}
                  >
                    Units
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === "item_count" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "item_count"}
                    direction={orderBy === "item_count" ? order : "asc"}
                    onClick={() => handleSort("item_count")}
                  >
                    Items
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === "status" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "status"}
                    direction={orderBy === "status" ? order : "asc"}
                    onClick={() => handleSort("status")}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === "created_at" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "created_at"}
                    direction={orderBy === "created_at" ? order : "asc"}
                    onClick={() => handleSort("created_at")}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && uomTypes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredAndSortedUomTypes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No UOM types found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedUomTypes.map((uomType) => (
                  <TableRow key={uomType.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {uomType.type_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {uomType.base_unit?.unit_name || "Unkown Base"} (
                        {uomType.base_unit?.unit_symbol || ""})
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {uomType.all_units?.length || 0} units
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {uomType.item_count} units
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(uomType)}</TableCell>
                    <TableCell>{formatDate(uomType.created_at)}</TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "center",
                        }}
                      >
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(
                                `/inventory/uoms/form?mode=view&id=${uomType.id}`
                              )
                            }
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>

                        {uomType.is_record_active ? (
                          <Box display="flex">
                            <Tooltip title="Edit UOM Type">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  navigate(
                                    `/inventory/uoms/form?mode=edit&id=${uomType.id}`
                                  )
                                }
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="De-activate UOM Type">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(uomType.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box display="flex">
                            <Tooltip title="Re-activate UOM Type">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleRestore(uomType.id)}
                              >
                                <RestoreIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={paginationDefaults.allRowOptions}
          component="div"
          count={totalCount}
          rowsPerPage={pagination.size}
          page={pagination.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};
